import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import CustomSelect from './widgets/CustomSelect';
import { parseSalaryImportFile, payPeriodFromDate, buildPayPeriodOptions } from '../utils/parseSalaryImportFile';
import '../styles/EmployeePaymentImportModal.css';

const DEFAULT_PAID_THROUGH_ACCOUNT = '4691741000000137021';
const IMPORT_URL = `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/payments/import`;
const STEP_UPLOAD = 'upload';
const STEP_CONFIRM = 'confirm';
const STEP_PREVIEW = 'preview';
const STEP_DONE = 'done';

const todayDateInput = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${today.getFullYear()}-${month}-${day}`;
};

const formatMoney = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const requestErrorMessage = (error, fallback) =>
  error?.response?.data?.error || fallback;

const EmployeePaymentImportModal = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState(STEP_UPLOAD);
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState(null);
  const [paidThroughAccount, setPaidThroughAccount] = useState(DEFAULT_PAID_THROUGH_ACCOUNT);
  const [paymentDate, setPaymentDate] = useState(todayDateInput());
  const [payPeriod, setPayPeriod] = useState(payPeriodFromDate(todayDateInput()));
  const [createExpense, setCreateExpense] = useState(true);
  const [parseError, setParseError] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [isWorking, setIsWorking] = useState(false);
  const [actionError, setActionError] = useState('');
  const [imported, setImported] = useState(false);

  const payPeriodOptions = useMemo(() => {
    const options = buildPayPeriodOptions();
    if (payPeriod && !options.includes(payPeriod)) {
      return [payPeriod, ...options];
    }
    return options;
  }, [payPeriod]);

  const handleClose = () => {
    if (isWorking) return;
    if (imported) {
      onSuccess();
      return;
    }
    onClose();
  };

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && !isWorking) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isWorking, imported]);

  const resetPreview = () => {
    setPreview(null);
    setResult(null);
    setActionError('');
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setParseError('');
    resetPreview();
    try {
      const buffer = await file.arrayBuffer();
      const parsedFile = parseSalaryImportFile(buffer, file.name);
      setParsed(parsedFile);
      setFileName(file.name);
      const nextDate = parsedFile.fileDate || todayDateInput();
      setPaymentDate(nextDate);
      setPayPeriod(payPeriodFromDate(nextDate));
      setStep(STEP_CONFIRM);
    } catch (error) {
      setParsed(null);
      setFileName('');
      setParseError(error.message || 'Could not read this file.');
    }
  };

  const buildPayload = (dryRun) => ({
    paid_through_account: paidThroughAccount,
    payment_date: paymentDate,
    pay_period: payPeriod,
    create_expense: createExpense,
    dry_run: dryRun,
    rows: (parsed?.rows || []).map((row) => ({
      employee_id: row.employee_id,
      employee_name: row.employee_name,
      amount: row.amount,
    })),
  });

  const handlePreview = async () => {
    if (!parsed) return;
    if (!paidThroughAccount) {
      setActionError('Please choose the paid through account.');
      return;
    }
    if (!paymentDate) {
      setActionError('Please choose the payment date.');
      return;
    }
    if (!payPeriod) {
      setActionError('Please choose the pay period.');
      return;
    }

    setIsWorking(true);
    setActionError('');
    try {
      const response = await axios.post(IMPORT_URL, buildPayload(true));
      setPreview(response.data);
      setStep(STEP_PREVIEW);
    } catch (error) {
      const data = error.response?.data;
      if (data?.errors?.length) {
        setPreview(data);
        setStep(STEP_PREVIEW);
      } else {
        setActionError(requestErrorMessage(error, 'Could not check this file. Please try again.'));
      }
    } finally {
      setIsWorking(false);
    }
  };

  const handleImport = async () => {
    if (preview?.ok === false) return;

    setIsWorking(true);
    setActionError('');
    try {
      const response = await axios.post(IMPORT_URL, buildPayload(false));
      setResult(response.data);
      setImported(true);
      setStep(STEP_DONE);
    } catch (error) {
      const data = error.response?.data;
      if (data?.errors?.length) {
        setPreview(data);
        setActionError(data.error || 'Please fix the problems below before importing.');
      } else {
        setActionError(requestErrorMessage(error, 'Could not import these payments. Please try again.'));
      }
    } finally {
      setIsWorking(false);
    }
  };

  const previewRows = preview?.rows || parsed?.rows || [];
  const previewErrors = preview?.errors || [];
  const previewWarnings = preview?.warnings || [];
  const canImport = Boolean(preview?.ok) && previewErrors.length === 0 && !isWorking;

  return createPortal(
    <div className="salary-import-overlay" onClick={handleClose}>
      <div
        className="salary-import-modal"
        role="dialog"
        aria-labelledby="salary-import-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="salary-import-header">
          <h2 id="salary-import-title">Import Salaries</h2>
          <button type="button" className="salary-import-close" onClick={handleClose} disabled={isWorking}>
            ×
          </button>
        </div>

        <div className="salary-import-body">
          {step === STEP_UPLOAD && (
            <>
              <p className="salary-import-help">
                Choose this month&apos;s salary file. The file should have Employee ID and Salary columns.
              </p>
              <label className="salary-import-file-label">
                Choose file
                <input
                  type="file"
                  accept=".xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                  onChange={handleFileChange}
                />
              </label>
              {parseError ? <div className="salary-import-error">{parseError}</div> : null}
            </>
          )}

          {step === STEP_CONFIRM && parsed && (
            <>
              <p className="salary-import-help">
                Check the paid through account, payment date, pay period, and whether to create the expense.
              </p>
              <div className="salary-import-summary">
                File: {fileName} · {parsed.rows.length} people · Total {formatMoney(parsed.totalAmount)}
              </div>
              {parsed.errors.length > 0 ? (
                <div className="salary-import-error">
                  {parsed.errors.map((error) => (
                    <div key={error}>{error}</div>
                  ))}
                </div>
              ) : null}

              <label className="salary-import-field">
                Paid through account
                <CustomSelect
                  name="paid_through_account"
                  value={paidThroughAccount}
                  onChange={(_name, value) => {
                    setPaidThroughAccount(value || '');
                    resetPreview();
                  }}
                  endpoint="/hospital-portal/api/employees/bank-accounts/paid-through-accounts"
                  valueField="account_number"
                  labelField="account_name"
                  placeholder="Choose account"
                  isClearable={false}
                  isSearchable
                />
              </label>

              <label className="salary-import-field">
                Payment date
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(event) => {
                    const nextDate = event.target.value;
                    setPaymentDate(nextDate);
                    setPayPeriod(payPeriodFromDate(nextDate));
                    resetPreview();
                  }}
                />
              </label>

              <label className="salary-import-field">
                Pay period
                <select
                  value={payPeriod}
                  onChange={(event) => {
                    setPayPeriod(event.target.value);
                    resetPreview();
                  }}
                >
                  {payPeriodOptions.map((period) => (
                    <option key={period} value={period}>
                      {period}
                    </option>
                  ))}
                </select>
              </label>

              <div className="salary-import-readonly">
                <span>Payment reason</span>
                <strong>Salary</strong>
              </div>

              <label className="salary-import-checkbox">
                <input
                  type="checkbox"
                  checked={createExpense}
                  onChange={(event) => setCreateExpense(event.target.checked)}
                />
                Create the expense after import
              </label>
              {actionError ? <div className="salary-import-error">{actionError}</div> : null}
            </>
          )}

          {step === STEP_PREVIEW && (
            <>
              <p className="salary-import-help">
                Review the list. You can still import if someone is inactive or already has a payment for {payPeriod}.
              </p>
              <div className="salary-import-summary">
                {previewRows.length} people · Total {formatMoney(preview?.total_amount || parsed?.totalAmount)} · {payPeriod}
              </div>
              {previewErrors.length > 0 ? (
                <div className="salary-import-error">
                  {previewErrors.map((error) => (
                    <div key={error.employee_id ? `${error.employee_id}-${error.message}` : error.message || error}>
                      {error.message || error}
                    </div>
                  ))}
                </div>
              ) : null}
              {previewWarnings.length > 0 ? (
                <div className="salary-import-warning">
                  {previewWarnings.map((warning) => (
                    <div key={warning.employee_id ? `${warning.employee_id}-${warning.message}` : warning.message || warning}>
                      {warning.message || warning}
                    </div>
                  ))}
                </div>
              ) : null}
              {previewErrors.length === 0 ? (
                <div className="salary-import-ok">Ready to import. Payment reason will be Salary.</div>
              ) : null}
              <div className="salary-import-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row) => (
                      <tr key={row.employee_id}>
                        <td>{row.employee_id}</td>
                        <td>{row.employee_name}</td>
                        <td>{formatMoney(row.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {actionError ? <div className="salary-import-error">{actionError}</div> : null}
            </>
          )}

          {step === STEP_DONE && result && (
            <>
              <p className="salary-import-help">
                Created {result.created_count} salary payments.
              </p>
              <div className="salary-import-ok">
                Note saved on every payment: {result.import_note}
              </div>
              {result.expense_requested && result.expense_error ? (
                <div className="salary-import-error">
                  Payments were saved, but the expense was not created: {result.expense_error}.
                  Use Create Expense on the payment list to try again.
                </div>
              ) : null}
              {result.expense_requested && !result.expense_error ? (
                <div className="salary-import-ok">The expense request was sent.</div>
              ) : null}
              {!result.expense_requested ? (
                <div className="salary-import-summary">
                  Expense was not created. You can still use Create Expense later.
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="salary-import-footer">
          {step === STEP_UPLOAD && (
            <button type="button" className="salary-import-secondary" onClick={handleClose}>
              Cancel
            </button>
          )}
          {step === STEP_CONFIRM && (
            <>
              <button type="button" className="salary-import-secondary" onClick={() => setStep(STEP_UPLOAD)} disabled={isWorking}>
                Back
              </button>
              <button
                type="button"
                className="salary-import-primary"
                onClick={handlePreview}
                disabled={isWorking || !parsed || parsed.errors.length > 0 || !payPeriod}
              >
                {isWorking ? 'Checking...' : 'Review list'}
              </button>
            </>
          )}
          {step === STEP_PREVIEW && (
            <>
              <button type="button" className="salary-import-secondary" onClick={() => setStep(STEP_CONFIRM)} disabled={isWorking}>
                Back
              </button>
              <button type="button" className="salary-import-primary" onClick={handleImport} disabled={!canImport}>
                {isWorking ? 'Importing...' : 'Import salaries'}
              </button>
            </>
          )}
          {step === STEP_DONE && (
            <button type="button" className="salary-import-primary" onClick={handleClose}>
              Done
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EmployeePaymentImportModal;
